define(function(require, exports, module) {
    'use strict';

    var _ = require('underscore');
    var i18n = require('i18n');
    var unicode = require('util/unicode.util');
    var mathUtil = require('util/math.util');
    var config = require('config');
    var alphaReSrc = unicode.alphabeticRe.source.split('[');
    var rePlaceholder = '___placeholder___';
    var alphaReTmpl = '^' + alphaReSrc[0] + '[' + rePlaceholder + alphaReSrc.slice(1).join('[') + '+$';

    // MCBB-604: in ios12 on mobile devices when user tap apostrophe on a keyboard after a word a (’) (U+2019)
    // `right single quotation mark` symbol is produced instead. This is syntactically incorrect, since this symbol
    // is actually a quotation mark that should be used to denote a quote and it should have an opening left quote.
    // In ios, however, it's used as apostrophe by default.
    // Considering all above - there's no single unicode subrange to include this symbol, so we just hardcode it.
    var firstLastNameRe = new RegExp('^(?! )' + alphaReTmpl.replace(rePlaceholder, ',.\'’\\-\\s'));
    var postcodeRe = new RegExp('^(?! )' + alphaReTmpl.replace(rePlaceholder, '\\s\\d_\\-/#\\\\'));
    var addressRe = new RegExp('^(?! )' + alphaReTmpl.replace(rePlaceholder, '.,#&_/\'’\\-\\d\\s\\\\'));
    var cityRe = new RegExp('^(?! )' + alphaReTmpl.replace(rePlaceholder, '\\d\\-.,\'’\\s'));
    var stateRe = new RegExp('^(?! )' + alphaReTmpl.replace(rePlaceholder, '\\-.,\\s'));
    const moneyRegex = /^((\d+[.,]?\d{0,2}$)|([.,]\d{1,2}$))/;

    function datePickerPrepareData($inputs) {
        const dateParts = $inputs
            .map((input) => input.name.replace(/_.+/g, ''))
            .reduce((result, name, index) => ({
                ...result,
                [name]: parseInt($inputs[index].value)
            }), {});

        const areAllInputsEmpty = Object.values(dateParts).every((datePart) => isNaN(datePart));

        const areAllInputsFilled = Object.values(dateParts).every((datePart) => !isNaN(datePart));

        return {
            date: dateParts,
            areAllInputsEmpty: areAllInputsEmpty,
            areAllInputsFilled: areAllInputsFilled
        };
    }

    var adultDateCheck = function(data) {
        var dtagMinAge = config.get('dynamicTags.DTAG_USER_MIN_AGE_VALIDATION');
        var minAge = dtagMinAge ? Number(dtagMinAge) : 18;
        var adultDate = new Date(data.date.year + minAge, data.date.month - 1, data.date.day);

        return adultDate <= new Date();
    };

    var dateOfBirthValidateFn = function($inputs, { isBirthdateRequired } = {}) {
        const data = datePickerPrepareData($inputs);

        if (!isBirthdateRequired && data.areAllInputsEmpty) {
            return true;
        }

        if (!data.areAllInputsFilled) {
            this.message = i18n.t('playtech.mobile.system.user.tooltip.birthdate.required');
            return false;
        }

        return adultDateCheck(data);
    };

    var addressLookupSwissValidateFn = function($inputs, c, form) {
        const input = $inputs[0];
        const dataset = input.dataset;
        const controlType = dataset.controlType;
        const customControl = _.findWhere(form._customControls, _.pick(dataset, 'id'));
        const defaultErrorMessage = 'playtech.mobile.registration.addressLookupSwiss.required';
        const innerFields = controlType === 'streetName' || controlType === 'houseNumber'
            ? [controlType]
            : ['zip', 'city', 'streetName', 'houseNumber'];

        const isEmptySomeRequired = innerFields.some((fieldName) =>
            'required' in form.options.validationConfig[fieldName] && !form.data()[fieldName]
        );

        const controlValidationRules = controlType === 'zipCity'
            ? customControl.options.validationRules
            : form.options.validationConfig[controlType];

        const errorMessage = _.property('message')(
            _.findWhere(controlValidationRules, { type: 'required' })
        );

        if (isEmptySomeRequired) {
            this.message = i18n.t(errorMessage || defaultErrorMessage);
            return false;
        }

        return true;
    };

    var addressLookupValidateFn = function($inputs, c, form) {
        var input = $inputs[0];
        var formData = form.data();
        var dataset = input.dataset;
        var customControl = form._customControls.find(ctrl => ctrl.id === dataset.id);

        var isEmptySomeRequired = customControl.options.innerFields.some(field => {
            return 'required' in form.options.validationConfig[field.name] && !formData[field.name];
        });

        if (isEmptySomeRequired) {
            var defaultErrorMessage = 'playtech.mobile.system.user.tooltip.addressLookup.required';
            var controlValidationRules = customControl.options.validationRules;
            var ruleRequired = controlValidationRules.find(rule => rule.type === 'required');

            this.message = i18n.t(ruleRequired?.message || defaultErrorMessage);

            return false;
        }

        return true;
    };

    var validatePhoneNumber = function(isPhoneRequired, $inputs) {
        var originPhoneValue = $inputs.map(_.property('value')).join('').trim();
        var phone = originPhoneValue.replace(/\+/g, '');
        var area = _.find($inputs, function(input) {
            return input.name === 'cellphone_area';
        });
        var areaValue;

        if (area) {
            areaValue = area.value.trim().replace(/\+/g, '');
            if (areaValue.length < 1 || areaValue.length > 4) {
                this.message = i18n.t('playtech.mobile.system.user.tooltip.area.length');
                return false;
            }
        }

        if (isPhoneRequired && !phone) {
            this.message = i18n.t('playtech.mobile.system.user.tooltip.phoneWithArea.required');
            return false;
        }

        if (isPhoneRequired && (phone.length < 7 || phone.length > 15)) {
            this.message = i18n.t('playtech.mobile.system.user.tooltip.phoneWithArea.length');
            return false;
        }

        return /^\+?[0-9]+$/.test(originPhoneValue);
    };

    var checkRomanianID = function($inputs, conf, form, state, event, data) {
        //COR18-63 Romanian regulations
        if (!data.birthdate || !data.sex) {
            this.message = i18n.t('playtech.mobile.system.user.tooltip.romanianID.emptyGenderBirth');
            return false;
        }
        var value = $inputs.val();
        var idSplits = value
            .split(/(\w{1})(\w{6})(\w{2})(\w{3})(\w{1})/)
            .filter(function(portion) {
                return portion !== '';
            });
        var idGenderCentury = idSplits[0];
        var idBirthDate = idSplits[1];
        var idCountryZone = idSplits[2];
        var idSerialNumber = idSplits[3];
        var idCheckSum = idSplits[4];

        var dateSplits = data.birthdate.split('-');
        var year = dateSplits[0];
        var month = dateSplits[1];
        var day = dateSplits[2];

        var male = data.sex === 'M';
        var female = data.sex === 'F';

        var verifyGenderCentury = function() {
            var id = Number(idGenderCentury);
            var y = Number(year);
            if (male && id === 7 || female && id === 8 || id === 9) {
                return true;
            }
            if (y >= 1900 && y <= 1999) {
                return male && id === 1 || female && id === 2;
            }
            if (y >= 1800 && y <= 1899) {
                return male && id === 3 || female && id === 4;
            }
            if (y >= 2000) {
                return male && id === 5 || female && id === 6;
            }
        };
        var verifyBirthDate = function() {
            var dateFormatted = year.slice(-2) + month + day;// format date in YYMMDD'
            return idBirthDate === dateFormatted;
        };
        var verifyCountryZone = function() {
            var id = parseInt(idCountryZone);

            return id === 80 || id === 99 || id >= 1 && id <= 52;
        };
        var verifySerialNumber = function() {
            return idSerialNumber.length === 3;
        };
        var verifyCheckSum = function() {
            var control = [2, 7, 9, 1, 4, 6, 3, 5, 8, 2, 7, 9];
            var slicedIdArr = value.slice(0, 12).split('').map(Number);
            var checkSumValue = slicedIdArr.reduce(function(memo, item, index) {
                memo = memo + item * control[index];
                return memo;
            }, 0);

            var reminder = checkSumValue % 11;
            reminder = reminder === 10 ? 1 : reminder;

            return reminder === Number(idCheckSum);
        };

        return verifyGenderCentury()
            && verifyBirthDate()
            && verifyCountryZone()
            && verifySerialNumber()
            && verifyCheckSum();
    };

    var checkPesel = function($inputs, conf, form, state, event, data) {
        // although reg builder won't allow to do this we'll make this fail-proof check
        if (!data.birthdate || !data.sex) {
            return false;
        }

        var pesel = $inputs.val();

        if (!/^\d{11}$/.test(pesel)) {
            return false;
        }

        var peselSplits = pesel
            .split(/(\w{2})(\w{2})(\w{2})(\w{4})(\w)/)
            .filter(function(part) {
                return part !== '';
            })
            .map(function(part) {
                return Number(part);
            });
        var peselRR = peselSplits[0];
        var peselMM = peselSplits[1];
        var peselDD = peselSplits[2];
        var peselPPPP = peselSplits[3];
        var peselK = peselSplits[4];

        var dateSplits = data.birthdate.split('-');
        var year = Number(dateSplits[0]);
        var month = Number(dateSplits[1]);
        var day = Number(dateSplits[2]);

        var checkPeselYear = function() {
            return peselRR === year % 100;
        };

        var checkPeselMonth = function() {
            var shift;

            if (year >= 1900 && year <= 1999) {
                shift = 0;
            } else if (year >= 1800 && year <= 1899) {
                shift = 80;
            } else if (year >= 2000 && year <= 2099) {
                shift = 20;
            } else if (year >= 2100 && year <= 2199) {
                shift = 40;
            } else if (year >= 2200 && year <= 2299) {
                shift = 60;
            }

            return peselMM === month + shift;
        };

        var checkPeselDay = function() {
            return peselDD === day;
        };

        var checkPeselGender = function() {
            var oddity = data.sex === 'M'
                ? 1
                : 0;

            return peselPPPP % 2 === oddity;
        };

        var checkPeselCheckSum = function() {
            var weights = [1, 3, 7, 9, 1, 3, 7, 9, 1, 3];
            var checkSum = pesel
                .split('')
                .slice(0, 10)
                .map(function(peselDigit, index) {
                    return Number(peselDigit) * weights[index];
                })
                .reduce(function(result, num) {
                    return result + num;
                }, 0);

            checkSum = (10 - checkSum % 10) % 10;

            return peselK === checkSum;
        };

        return checkPeselYear()
            && checkPeselMonth()
            && checkPeselDay()
            && checkPeselGender()
            && checkPeselCheckSum();
    };

    var checkControlLetter = function(digits, controlLetter) {
        var controlLettersList = 'TRWAGMYFPDXBNJZSQVHLCKE'.split('');

        return controlLettersList[parseInt(digits) % 23] === controlLetter;
    };

    var checkDNI = function(id) {
        var regExp = /^(\d{1,8})([A-Z])$/;

        var isValid = regExp.test(id);
        var match = id.match(regExp);

        return isValid && checkControlLetter(match[1], match[2]);
    };

    var checkNIE = function(id) {
        var prefixList = ['X', 'Y', 'Z'];
        var regExp = /^([XYZ])(\d{7})([A-Z])$/;

        var isValid = regExp.test(id);
        var match = id.match(regExp);

        return isValid && checkControlLetter(prefixList.indexOf(match[1]).toString() + match[2], match[3]);
    };

    var checkNIF = function(nif) {
        if (!['1', '2', '3', '5', '6', '8'].includes(nif.substr(0, 1)) &&
            !['45', '70', '71', '72', '77', '79', '90', '91', '98', '99'].includes(nif.substr(0, 2))) {
            return false;
        }

        var total = nif[0] * 9 + nif[1] * 8 + nif[2] * 7 + nif[3] * 6 + nif[4] * 5 + nif[5] * 4 + nif[6] * 3
            + nif[7] * 2;

        var modulo11 = total - parseInt(total / 11) * 11;
        var comparador = modulo11 === 1 || modulo11 === 0 ? 0 : 11 - modulo11;

        return Number(nif[8]) === comparador;
    };

    var checkSpanishRules = function($inputs, conf, form, state, event, data) {
        if (!(data.countrycode && data.citizenship)) {
            this.message = i18n.t('playtech.mobile.system.user.tooltip.spanishID.emptyCountryOrNational');
            return false;
        }

        var value = $inputs.val().trim();
        var countrycode = data.countrycode;
        var citizenship = data.citizenship;
        var addMissingNumbers = function() {
            var length = value.length;
            var DNIlength = 9;

            if (length < DNIlength) {
                $inputs[0].value = String('00000000' + value).slice(-DNIlength);
            }
        };

        if (citizenship !== 'ES' && countrycode !== 'ES') {
            return true;
        }

        if (citizenship === 'ES') {
            if (countrycode !== 'ES') {
                if (!value.length) {
                    return true;
                }
            }

            if (countrycode === 'ES') {
                if (!value.length) {
                    this.message = i18n.t('playtech.mobile.system.user.tooltip.spanishID.required');
                    return false;
                }
            }

            if (!checkDNI(value)) {
                this.message = i18n.t('playtech.mobile.system.user.tooltip.spanishID.wrong', 'DNI');
                return false;
            }

            addMissingNumbers();

            return true;
        }

        if (citizenship !== 'ES' && countrycode === 'ES') {
            if (!value.length) {
                this.message = i18n.t('playtech.mobile.system.user.tooltip.spanishID.required');
                return false;
            }

            if (!checkNIE(value)) {
                this.message = i18n.t('playtech.mobile.system.user.tooltip.spanishID.wrong', 'NIE');
                return false;
            }

            return true;
        }
    };

    var checkPortugalRules = function($inputs, conf, form, state, event, data) {
        if (!data.citizenship) {
            this.message = i18n.t('playtech.mobile.system.user.tooltip.portugalID.emptyNational');
            return false;
        }

        var value = $inputs.val().trim();
        var isPortugal = data.citizenship === 'PT';

        if (value.length) {
            this.message = i18n.t('playtech.mobile.system.user.tooltip.portugalID.wrong');

            return checkNIF(value);
        }

        this.message = i18n.t('playtech.mobile.system.user.tooltip.portugalID.required');

        return !isPortugal;
    };

    var checkSouthAfricanRules = function($inputs, conf, form, state, event, data) {
        const value = $inputs.val().trim();

        const birthdate = data.birthdate || '';
        const [yyyy, mm, dd] = birthdate.split('-');
        const yy = yyyy.substring(2);
        const dateOfBirthPart = value.substring(0, 6);

        if (dateOfBirthPart !== `${yy}${mm}${dd}`) {
            this.message = i18n.t('playtech.mobile.system.user.tooltip.southafricanid.wrongDateOfBirth');

            return false;
        }

        const actualSum = value.split('').reduceRight((sum, val, i, arr) => {
            const rightToLeftIndex = arr.length - i;
            const multiplier = rightToLeftIndex % 2 ? 1 : 2;
            const add = multiplier * Number(val);

            return sum + (add > 9 ? add - 9 : add);
        }, 0);

        return actualSum % 10 === 0;
    };

    var validateIban = function($inputs, conf, form, state, event, data) {
        this.message = i18n.t('playtech.mobile.registration.iban.error.doubleCheck');
        var value = $inputs.val().trim();

        if (!data.countrycode || !value || !/\d+/.test(value)) {
            return false;
        }

        var countryCodeNum = Array.from(data.countrycode)
            .map(function(char) {
                return char.charCodeAt(0) - 55;
            })
            .join('');

        var ibanMod = value.substring(2) + countryCodeNum + value.substring(0, 2);

        return mathUtil.modulo(ibanMod, 97) === 1;
    };

    var isZeroAndIsInput = function(value, isInputType) {
            var isInvalidValue = !value && value !== 0;
            var isZeroValue = value === 0 && isInputType;
            return !(isInvalidValue || isZeroValue);
        };

    var isZeroAndIsDropdown = function(value, isInputType) {
        return value === 0 && !isInputType;
    };

    var isFitLimitAmount = {
        'session': function(data, isInputType) {
            var value = data.session;
            // deposit limit has editable dropdown type
            // 'isInputType' param use for loss/bet limits
            var isDepositLimit = _.isUndefined(isInputType);

            if (!isDepositLimit && !isZeroAndIsInput(value, isInputType)) {
                return false;
            }
            if (!isDepositLimit && isZeroAndIsDropdown(value, isInputType)) {
                return true;
            }

            if (data.day && data.day < value) {
                return false;
            }
            if (data.week && data.week < value) {
                return false;
            }
            if (data.month && data.month < value) {
                return false;
            }

            return true;
        },
        'day': function(data, isInputType) {
            var value = data.day;

            if (!isZeroAndIsInput(value, isInputType)) {
                return false;
            }
            if (isZeroAndIsDropdown(value, isInputType)) {
                return true;
            }
            if (data.week && data.week < value) {
                return false;
            }
            if (data.month && data.month < value) {
                return false;
            }
            if (data.session && data.session > value) {
                return false;
            }

            return true;
        },
        'week': function(data, isInputType) {
            var value = data.week;

            if (!isZeroAndIsInput(value, isInputType)) {
                return false;
            }
            if (isZeroAndIsDropdown(value, isInputType)) {
                return true;
            }
            if (data.day && data.day > value) {
                return false;
            }
            if (data.month && data.month < value) {
                return false;
            }
            if (data.session && data.session > value) {
                return false;
            }

            return true;
        },
        'month': function(data, isInputType) {
            var value = data.month;

            if (!isZeroAndIsInput(value, isInputType)) {
                return false;
            }
            if (isZeroAndIsDropdown(value, isInputType)) {
                return true;
            }
            if (data.day && data.day > value) {
                return false;
            }
            if (data.week && data.week > value) {
                return false;
            }
            if (data.session && data.session > value) {
                return false;
            }

            return true;
        }
    };

    var isAscendingLimitAmount = {
        'session': function(data) {
            var value = data.session;

            if (data.day && data.day < value) {
                return false;
            }
            if (data.week && data.week < value) {
                return false;
            }
            if (data.month && data.month < value) {
                return false;
            }

            return true;
        },
        'day': function(data) {
            var value = data.day;

            if (data.session && data.session > value) {
                return false;
            }
            if (data.week && data.week < value) {
                return false;
            }
            if (data.month && data.month < value) {
                return false;
            }

            return true;
        },
        'week': function(data) {
            var value = data.week;

            if (data.session && data.session > value) {
                return false;
            }
            if (data.day && data.day > value) {
                return false;
            }
            if (data.month && data.month < value) {
                return false;
            }

            return true;
        },
        'month': function(data) {
            var value = data.month;

            if (data.session && data.session > value) {
                return false;
            }
            if (data.day && data.day > value) {
                return false;
            }
            if (data.week && data.week > value) {
                return false;
            }

            return true;
        }
    };

    module.exports = {
        firstLastNameRe,
        postcodeRe,
        addressRe,
        cityRe,
        stateRe,

        dateOfBirthValidateFn,

        addressLookupSwissValidateFn,
        addressLookupValidateFn,

        validatePhoneNumber,

        checkRomanianID,
        checkPesel,
        checkSpanishRules,
        checkPortugalRules,
        checkSouthAfricanRules,

        validateIban,

        isFitLimitAmount,
        isAscendingLimitAmount,
        moneyRegex
    };
});
